import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, ChevronDown, FileCheck, Loader2, Plus, Upload, X } from 'lucide-react'
import { useFeatureFlags } from '../contexts/FeatureFlagContext'

// --- Types ---

type CropType = 'corn' | 'soybeans' | 'wheat' | 'alfalfa' | 'potatoes' | 'sugar_beets' | 'dry_beans'
type EquipmentType = 'in_furrow' | 'two_by_two' | 'sidedress' | 'sprayer'
type SprayerOwnership = 'owns' | 'neighbor_friend' | 'third_party_retailer'
type SprayMethod = 'ground' | 'aerial'

interface EquipmentWillingness {
  has: boolean | null
  uses: boolean | null
  willing: boolean | null
}
type NitrogenForm = 'uan' | 'anhydrous'
type FungicidePlan = 'vt_r3_ground' | 'vt_r3_aerial' | 'none'

interface CornInputs {
  aph: number | null
  yield_goal: number | null
  willing_to_sidedress: boolean
  uan_gallons_2x2: number | null
  uan_gallons_sidedress: number | null
  nitrogen_form: NitrogenForm | null
  sulfur_already_purchased: boolean
  uses_manure: boolean
  fungicide_plan: FungicidePlan | null
  location_state: string | null
  foliar_water_volume_gal: number | null
  fungicide_water_volume_gal: number | null
}

type RowSpacing = 'drilled' | '15' | '20' | '30' | '40' | 'twin'
type SCNHistory = 'yes' | 'no' | 'unknown'

type R3ApplicationMethod = 'ground' | 'aerial'

interface SoybeanInputs {
  aph: number | null
  yield_goal: number | null
  use_aph_or_goal: 'aph' | 'goal' | null
  row_spacing: RowSpacing | null
  has_in_furrow_planter: boolean
  scn_history: SCNHistory | null
  using_scn_seed_treatment: boolean
  currently_uses_infurrow_fertilizer: boolean
  wants_npk_starter: boolean
  foliar_herbicide_water_gal: number | null
  r3_application_method: R3ApplicationMethod | null
  r3_water_volume_gal: number | null
}

type SugarBeetYieldLevel = '26-30' | '30-40'

interface WheatInputs {
  yield_goal: number | null
  nitrogen_passes: 1 | 2
  wild_onion_garlic: boolean
}

interface SugarBeetInputs {
  yield_level: SugarBeetYieldLevel | null
  has_in_furrow: boolean
  has_2x2: boolean
  current_2x2_uan_rate: number | null
  current_2x2_1034_rate: number | null
  sidedresses: boolean
  sidedress_uan_gallons: number | null
  cls_spray_count: number
  cls_water_carrier_gallons: number | null
}

type DryBeanYieldRange = '25-28' | '28-31' | '31-34' | '35+'

interface DryBeanInputs {
  yield_goal_range: DryBeanYieldRange | null
  sugar_beets_last_year: boolean
  herbicide_passes: 1 | 2
  herbicide_water_gallons: number | null
  fungicide_passes: 1 | 2
  fungicide_water_gallons: number | null
}

interface CurrentProduct {
  product: string
  rate: number
  unit: 'gallons' | 'quarts' | 'ounces'
}

interface CropProducts {
  in_furrow: CurrentProduct[]
  two_by_two: CurrentProduct[]
  sidedress: CurrentProduct[]
  uses_biological: boolean
  biological_product: string
  uses_insecticide: boolean
  uses_xyway: boolean
  xyway_rate: number | null
  vegetative_fungicide: boolean
  reproductive_fungicide: boolean
  deer_damage: boolean
  deer_damage_pct_acres: number | null
  weed_escapes: boolean
  weed_escape_acres: number | null
  corn_inputs: CornInputs | null
  soybean_inputs: SoybeanInputs | null
  wheat_inputs: WheatInputs | null
  sugar_beet_inputs: SugarBeetInputs | null
  dry_bean_inputs: DryBeanInputs | null
}

interface CropEntry {
  crop_type: CropType
  acres: number
  current_products: CropProducts
}

interface FormData {
  farm_name: string
  farmer_name: string
  location_state: string | null
  total_acres: number | null
  crops: CropEntry[]
  equipment: EquipmentType[]
  in_furrow_willingness: EquipmentWillingness
  two_by_two_willingness: EquipmentWillingness
  has_y_drop_attachment: boolean
  sprayer_ownership: SprayerOwnership
  spray_method: SprayMethod
  spray_water_volume_gal: number | null
  water_ph: number | null
  // Soil data
  phosphorus: number
  potassium: number
  organic_matter: number
  cec: number
  ph: number
  pct_fields_below_critical: number
  base_saturation_calcium: number | null
  base_saturation_magnesium: number | null
  base_saturation_potassium: number | null
  buffer_ph: number | null
  // Detailed soil breakdown (corn decision tree)
  calcium_ppm: number | null
  pct_fields_high_ph: number | null
  pct_fields_below_critical_p: number | null
  pct_fields_below_critical_k: number | null
  pct_fields_low_calcium: number | null
  soil_file_key: string | null
  skip_soil: boolean
}

const emptyCornInputs: CornInputs = {
  aph: null,
  yield_goal: null,
  willing_to_sidedress: true,
  uan_gallons_2x2: null,
  uan_gallons_sidedress: null,
  nitrogen_form: null,
  sulfur_already_purchased: false,
  uses_manure: false,
  fungicide_plan: null,
  location_state: null,
  foliar_water_volume_gal: null,
  fungicide_water_volume_gal: null,
}

const emptySoybeanInputs: SoybeanInputs = {
  aph: null,
  yield_goal: null,
  use_aph_or_goal: null,
  row_spacing: null,
  has_in_furrow_planter: false,
  scn_history: null,
  using_scn_seed_treatment: false,
  currently_uses_infurrow_fertilizer: false,
  wants_npk_starter: false,
  foliar_herbicide_water_gal: null,
  r3_application_method: null,
  r3_water_volume_gal: null,
}

const emptyWheatInputs: WheatInputs = {
  yield_goal: null,
  nitrogen_passes: 1,
  wild_onion_garlic: false,
}

const emptyDryBeanInputs: DryBeanInputs = {
  yield_goal_range: null,
  sugar_beets_last_year: false,
  herbicide_passes: 2,
  herbicide_water_gallons: null,
  fungicide_passes: 2,
  fungicide_water_gallons: null,
}

const emptySugarBeetInputs: SugarBeetInputs = {
  yield_level: null,
  has_in_furrow: false,
  has_2x2: false,
  current_2x2_uan_rate: null,
  current_2x2_1034_rate: null,
  sidedresses: false,
  sidedress_uan_gallons: null,
  cls_spray_count: 6,
  cls_water_carrier_gallons: null,
}

const emptyCropProducts: CropProducts = {
  in_furrow: [],
  two_by_two: [],
  sidedress: [],
  uses_biological: false,
  biological_product: '',
  uses_insecticide: false,
  uses_xyway: false,
  xyway_rate: null,
  vegetative_fungicide: false,
  reproductive_fungicide: false,
  deer_damage: false,
  deer_damage_pct_acres: null,
  weed_escapes: false,
  weed_escape_acres: null,
  corn_inputs: null,
  soybean_inputs: null,
  wheat_inputs: null,
  sugar_beet_inputs: null,
  dry_bean_inputs: null,
}

const initialFormData: FormData = {
  farm_name: '',
  farmer_name: '',
  location_state: null,
  total_acres: null,
  crops: [],
  equipment: [],
  in_furrow_willingness: { has: null, uses: null, willing: null },
  two_by_two_willingness: { has: null, uses: null, willing: null },
  has_y_drop_attachment: false,
  sprayer_ownership: 'owns',
  spray_method: 'ground',
  spray_water_volume_gal: null,
  water_ph: null,
  phosphorus: 0,
  potassium: 0,
  organic_matter: 0,
  cec: 0,
  ph: 0,
  pct_fields_below_critical: 0,
  base_saturation_calcium: null,
  base_saturation_magnesium: null,
  base_saturation_potassium: null,
  buffer_ph: null,
  calcium_ppm: null,
  pct_fields_high_ph: null,
  pct_fields_below_critical_p: null,
  pct_fields_below_critical_k: null,
  pct_fields_low_calcium: null,
  soil_file_key: null,
  skip_soil: false,
}

// Dynamic steps — "Current Products" repeats per crop, corn gets extra details step
function getSteps(crops: CropEntry[]) {
  const steps = [
    { id: 'farm', title: 'Farm Info' },
    { id: 'crops', title: 'Crops & Acres' },
    { id: 'equipment', title: 'Equipment' },
  ]
  for (let i = 0; i < crops.length; i++) {
    steps.push({
      id: `current-${i}`,
      title: `Products: ${cropLabels[crops[i].crop_type] || crops[i].crop_type}`,
    })
    if (crops[i].crop_type === 'corn') {
      steps.push({
        id: `corn-details-${i}`,
        title: 'Corn Program Details',
      })
    }
    if (crops[i].crop_type === 'soybeans') {
      steps.push({
        id: `soybean-details-${i}`,
        title: 'Soybean Program Details',
      })
    }
    if (crops[i].crop_type === 'wheat') {
      steps.push({
        id: `wheat-details-${i}`,
        title: 'Wheat Program Details',
      })
    }
    if (crops[i].crop_type === 'sugar_beets') {
      steps.push({
        id: `sugar-beet-details-${i}`,
        title: 'Sugar Beet Program Details',
      })
    }
    if (crops[i].crop_type === 'dry_beans') {
      steps.push({
        id: `dry-bean-details-${i}`,
        title: 'Dry Bean Program Details',
      })
    }
  }
  steps.push({ id: 'soil', title: 'Soil Data (Optional)' })
  return steps
}

const cropLabels: Record<CropType, string> = {
  corn: 'Corn',
  soybeans: 'Soybeans',
  wheat: 'Wheat',
  alfalfa: 'Alfalfa',
  potatoes: 'Potatoes',
  sugar_beets: 'Sugar Beets',
  dry_beans: 'Dry Beans',
}

const cropEmojis: Record<CropType, React.ReactNode> = {
  corn: '🌽',
  soybeans: '🫛',
  wheat: '🌾',
  alfalfa: '🌿',
  potatoes: '🥔',
  sugar_beets: <img src="/sugar-beet.png" alt="Sugar Beet" className="inline-block w-7 h-7" />,
  dry_beans: '🫘',
}

// --- Validation ---

function validateStep(stepId: string, formData: FormData, cropIndex: number): string[] {
  const errors: string[] = []

  if (stepId === 'farm') {
    if (!formData.farmer_name.trim()) errors.push('Please enter the farmer\'s name.')
    if (!formData.farm_name.trim()) errors.push('Please enter the farm name.')
  }

  if (stepId === 'crops') {
    if (formData.crops.length === 0) errors.push('Please select at least one crop.')
    for (const crop of formData.crops) {
      if (!crop.acres || crop.acres <= 0) {
        errors.push(`Please enter the number of acres for ${cropLabels[crop.crop_type]}.`)
      }
    }
  }

  if (stepId.startsWith('current-') && cropIndex >= 0 && cropIndex < formData.crops.length) {
    const cp = formData.crops[cropIndex].current_products
    const slotNames: Record<string, string> = {
      in_furrow: 'in-furrow',
      two_by_two: '2x2',
      sidedress: 'sidedress',
    }
    for (const slot of ['in_furrow', 'two_by_two', 'sidedress'] as const) {
      for (const p of cp[slot]) {
        if (!p.product.trim()) errors.push(`Please enter a product name for each ${slotNames[slot]} entry.`)
        if (!p.rate || p.rate <= 0) errors.push(`Please enter a rate greater than zero for each ${slotNames[slot]} product.`)
      }
    }
  }

  // Soil step is optional — only validate ranges if the user entered values
  if (stepId === 'soil') {
    if (formData.ph && (formData.ph < 3 || formData.ph > 10)) errors.push('Soil pH should be between 3.0 and 10.0.')
    if (formData.organic_matter && (formData.organic_matter < 0 || formData.organic_matter > 20)) errors.push('Organic matter should be between 0% and 20%.')
    if (formData.pct_fields_below_critical && (formData.pct_fields_below_critical < 0 || formData.pct_fields_below_critical > 100)) errors.push('Fields below critical should be between 0% and 100%.')
    if (formData.phosphorus && formData.phosphorus < 0) errors.push('Phosphorus cannot be negative.')
    if (formData.potassium && formData.potassium < 0) errors.push('Potassium cannot be negative.')
    if (formData.cec && formData.cec < 0) errors.push('CEC cannot be negative.')
  }

  return errors
}

export default function Questionnaire() {
  const navigate = useNavigate()
  const { sessionId: editSessionId } = useParams<{ sessionId?: string }>()
  const [searchParams] = useSearchParams()
  const isEditMode = Boolean(editSessionId)
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [stepErrors, setStepErrors] = useState<string[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [loadingInputs, setLoadingInputs] = useState(isEditMode)

  // Portal flow: pre-fill from query params (farmerId, farmerName, farmName)
  const portalFarmerId = searchParams.get('farmerId')
  const portalFarmerName = searchParams.get('farmerName')
  const portalFarmName = searchParams.get('farmName')

  // Pre-fill from query params on mount (portal "New Recommendation" flow)
  useEffect(() => {
    if (!isEditMode && (portalFarmerName || portalFarmName)) {
      setFormData(prev => ({
        ...prev,
        farmer_name: portalFarmerName || prev.farmer_name,
        farm_name: portalFarmName || prev.farm_name,
      }))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Edit mode: fetch existing inputs and pre-fill form
  useEffect(() => {
    if (!editSessionId) return
    let cancelled = false

    async function loadInputs() {
      try {
        const res = await fetch(`/api/sessions/${editSessionId}/inputs`)
        if (!res.ok) throw new Error('Failed to load session inputs')
        const data = await res.json()
        if (cancelled) return

        const q = data.questionnaire
        const soil = data.soil

        // Build crops from questionnaire
        const crops: CropEntry[] = (q?.crops ?? []).map((c: any) => ({
          crop_type: c.crop_type as CropType,
          acres: c.acres,
          current_products: {
            ...emptyCropProducts,
            ...c.current_products,
            corn_inputs: c.current_products?.corn_inputs
              ? { ...emptyCornInputs, ...c.current_products.corn_inputs }
              : null,
            soybean_inputs: c.current_products?.soybean_inputs
              ? { ...emptySoybeanInputs, ...c.current_products.soybean_inputs }
              : null,
            wheat_inputs: c.current_products?.wheat_inputs
              ? { ...emptyWheatInputs, ...c.current_products.wheat_inputs }
              : null,
            sugar_beet_inputs: c.current_products?.sugar_beet_inputs
              ? { ...emptySugarBeetInputs, ...c.current_products.sugar_beet_inputs }
              : null,
            dry_bean_inputs: c.current_products?.dry_bean_inputs
              ? { ...emptyDryBeanInputs, ...c.current_products.dry_bean_inputs }
              : null,
          },
        }))

        // Extract location_state from first corn crop's inputs
        const cornCrop = (q?.crops ?? []).find((c: any) => c.crop_type === 'corn')
        const locationState = cornCrop?.current_products?.corn_inputs?.location_state ?? null

        setFormData({
          farmer_name: q?.farmer_name ?? data.farmer_name ?? '',
          farm_name: q?.farm_name ?? data.farm_name ?? '',
          location_state: locationState,
          total_acres: null,
          crops,
          equipment: q?.equipment ?? [],
          in_furrow_willingness: { has: null, uses: null, willing: null },
          two_by_two_willingness: { has: null, uses: null, willing: null },
          has_y_drop_attachment: q?.has_y_drop_attachment ?? false,
          sprayer_ownership: q?.sprayer_ownership ?? 'owns',
          spray_method: q?.spray_method ?? 'ground',
          spray_water_volume_gal: q?.spray_water_volume_gal ?? null,
          water_ph: q?.water_ph ?? null,
          // Soil data
          phosphorus: soil?.phosphorus_ppm ?? 0,
          potassium: soil?.potassium_ppm ?? 0,
          organic_matter: soil?.organic_matter_pct ?? 0,
          cec: soil?.cec ?? 0,
          ph: soil?.ph ?? 0,
          pct_fields_below_critical: soil?.pct_fields_below_critical ?? 0,
          base_saturation_calcium: soil?.base_saturation_calcium_pct ?? null,
          base_saturation_magnesium: soil?.base_saturation_magnesium_pct ?? null,
          base_saturation_potassium: soil?.base_saturation_potassium_pct ?? null,
          buffer_ph: soil?.buffer_ph ?? null,
          calcium_ppm: soil?.calcium_ppm ?? null,
          pct_fields_high_ph: soil?.pct_fields_high_ph ?? null,
          pct_fields_below_critical_p: soil?.pct_fields_below_critical_p ?? null,
          pct_fields_below_critical_k: soil?.pct_fields_below_critical_k ?? null,
          pct_fields_low_calcium: soil?.pct_fields_low_calcium ?? null,
          soil_file_key: soil?.file_key ?? null,
          skip_soil: false,
        })
      } catch (err) {
        console.error('Error loading session inputs:', err)
        setSubmitError(err instanceof Error ? err.message : 'Failed to load inputs')
      } finally {
        if (!cancelled) setLoadingInputs(false)
      }
    }

    loadInputs()
    return () => { cancelled = true }
  }, [editSessionId])

  const steps = getSteps(formData.crops)
  const step = steps[currentStep]
  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === steps.length - 1
  const progress = ((currentStep + 1) / steps.length) * 100

  // Determine which "current products" crop index we're on
  const currentCropIndex = step?.id.startsWith('current-')
    ? parseInt(step.id.split('-')[1])
    : -1

  // Determine which "corn details" crop index we're on
  const cornDetailsCropIndex = step?.id.startsWith('corn-details-')
    ? parseInt(step.id.split('-')[2])
    : -1

  // Determine which "soybean details" crop index we're on
  const soybeanDetailsCropIndex = step?.id.startsWith('soybean-details-')
    ? parseInt(step.id.split('-')[2])
    : -1

  // Determine which "wheat details" crop index we're on
  const wheatDetailsCropIndex = step?.id.startsWith('wheat-details-')
    ? parseInt(step.id.split('-')[2])
    : -1

  // Determine which "sugar beet details" crop index we're on
  const sugarBeetDetailsCropIndex = step?.id.startsWith('sugar-beet-details-')
    ? parseInt(step.id.split('-')[3])
    : -1

  // Determine which "dry bean details" crop index we're on
  const dryBeanDetailsCropIndex = step?.id.startsWith('dry-bean-details-')
    ? parseInt(step.id.split('-')[3])
    : -1

  const handleNext = () => {
    const errors = validateStep(step.id, formData, currentCropIndex)
    if (errors.length > 0) {
      setStepErrors(errors)
      return
    }
    setStepErrors([])
    setSubmitError(null)
    if (isLastStep) {
      handleSubmit()
    } else {
      setCurrentStep(s => s + 1)
    }
  }

  const handleBack = () => {
    setStepErrors([])
    setSubmitError(null)
    if (isFirstStep) {
      navigate('/')
    } else {
      setCurrentStep(s => s - 1)
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      // In edit mode, reuse the existing session; otherwise create a new one
      let sessionId: string
      if (isEditMode && editSessionId) {
        sessionId = editSessionId
      } else {
        const sessionRes = await fetch('/api/sessions/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            farmer_name: formData.farmer_name,
            farm_name: formData.farm_name,
            ...(portalFarmerId ? { farmer_id: portalFarmerId } : {}),
          }),
        })
        if (!sessionRes.ok) throw new Error('Failed to create session')
        const session = await sessionRes.json()
        sessionId = session.id
      }
      const session = { id: sessionId }

      // Submit questionnaire (FarmQuestionnaireInput shape)
      const questionnaireRes = await fetch(`/api/sessions/${session.id}/questionnaire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          farmer_name: formData.farmer_name,
          farm_name: formData.farm_name,
          crops: formData.crops.map(c => ({
            crop_type: c.crop_type,
            acres: c.acres,
            current_products: {
              ...c.current_products,
              // Inject farm-level state into corn_inputs for the backend
              ...(c.current_products.corn_inputs ? {
                corn_inputs: { ...c.current_products.corn_inputs, location_state: formData.location_state },
              } : {}),
            },
          })),
          equipment: formData.equipment,
          has_y_drop_attachment: formData.has_y_drop_attachment,
          sprayer_ownership: formData.sprayer_ownership,
          spray_method: formData.spray_method,
          spray_water_volume_gal: formData.spray_water_volume_gal,
          water_ph: formData.water_ph,
          water_ph_tested: formData.water_ph != null,
        }),
      })
      if (!questionnaireRes.ok) {
        const detail = await questionnaireRes.json().catch(() => null)
        const msg = detail?.detail
          ? (Array.isArray(detail.detail) ? detail.detail.map((d: any) => d.msg).join(', ') : detail.detail)
          : 'Failed to submit questionnaire'
        throw new Error(msg)
      }

      // Submit soil data (only if the user entered any values and didn't skip)
      const hasSoilData = formData.phosphorus || formData.potassium || formData.organic_matter
        || formData.cec || formData.ph || formData.pct_fields_below_critical
      if (hasSoilData && !formData.skip_soil) {
        // Compute pct_fields_buffer_ph_low heuristic for manual single-value entry
        const pctBufferPhLow = formData.buffer_ph != null
          ? (formData.buffer_ph <= 6.8 ? 100 : 0)
          : null

        const soilRes = await fetch(`/api/sessions/${session.id}/soil`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            file_key: formData.soil_file_key,
            phosphorus_ppm: formData.phosphorus,
            potassium_ppm: formData.potassium,
            organic_matter_pct: formData.organic_matter,
            cec: formData.cec,
            ph: formData.ph || 6.5,
            buffer_ph: formData.buffer_ph,
            pct_fields_below_critical: formData.pct_fields_below_critical,
            pct_fields_buffer_ph_low: pctBufferPhLow,
            base_saturation_calcium_pct: formData.base_saturation_calcium,
            base_saturation_magnesium_pct: formData.base_saturation_magnesium,
            base_saturation_potassium_pct: formData.base_saturation_potassium,
            calcium_ppm: formData.calcium_ppm,
            pct_fields_high_ph: formData.pct_fields_high_ph,
            pct_fields_below_critical_p: formData.pct_fields_below_critical_p,
            pct_fields_below_critical_k: formData.pct_fields_below_critical_k,
            pct_fields_low_calcium: formData.pct_fields_low_calcium,
          }),
        })
        if (!soilRes.ok) {
          const detail = await soilRes.json().catch(() => null)
          const msg = detail?.detail
            ? (Array.isArray(detail.detail) ? detail.detail.map((d: any) => d.msg).join(', ') : detail.detail)
            : 'Failed to submit soil data'
          throw new Error(msg)
        }
      }

      // Run pipeline
      const runRes = await fetch(`/api/sessions/${session.id}/run`, {
        method: 'POST',
      })
      if (!runRes.ok) throw new Error('Failed to run recommendations')

      // Navigate to results
      navigate(`/results/${session.id}`)
    } catch (error) {
      console.error('Error submitting questionnaire:', error)
      setSubmitError(error instanceof Error ? error.message : 'Something went wrong')
      setIsSubmitting(false)
    }
  }

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (stepErrors.length > 0) setStepErrors([])
  }

  const toggleEquipment = (equip: EquipmentType) => {
    if (stepErrors.length > 0) setStepErrors([])
    setFormData(prev => {
      const next = prev.equipment.includes(equip)
        ? prev.equipment.filter(e => e !== equip)
        : [...prev.equipment, equip]
      // Clear y-drop if sidedress is deselected
      const hasYDrop = equip === 'sidedress' && !next.includes('sidedress')
        ? false
        : prev.has_y_drop_attachment
      // Clear spray fields if sprayer is deselected
      const sprayMethod = equip === 'sprayer' && !next.includes('sprayer')
        ? 'ground' as SprayMethod
        : prev.spray_method
      const sprayWater = equip === 'sprayer' && !next.includes('sprayer')
        ? null
        : prev.spray_water_volume_gal
      return { ...prev, equipment: next, has_y_drop_attachment: hasYDrop, spray_method: sprayMethod, spray_water_volume_gal: sprayWater }
    })
  }

  const updateWillingness = (
    field: 'in_furrow_willingness' | 'two_by_two_willingness',
    equip: EquipmentType,
    update: Partial<EquipmentWillingness>,
  ) => {
    if (stepErrors.length > 0) setStepErrors([])
    setFormData(prev => {
      const current = prev[field]
      const next: EquipmentWillingness = { ...current, ...update }

      // Reset downstream answers when upstream changes
      if ('has' in update) {
        if (!update.has) { next.uses = null; next.willing = null }
      }
      if ('uses' in update) {
        if (update.uses) { next.willing = null }
      }

      // Include equipment if: has AND (uses OR willing)
      const shouldInclude = next.has === true && (next.uses === true || next.willing === true)
      const isIncluded = prev.equipment.includes(equip)
      let equipment = prev.equipment
      if (shouldInclude && !isIncluded) {
        equipment = [...equipment, equip]
      } else if (!shouldInclude && isIncluded) {
        equipment = equipment.filter(e => e !== equip)
      }

      return { ...prev, [field]: next, equipment }
    })
  }

  const toggleCrop = (cropType: CropType) => {
    if (stepErrors.length > 0) setStepErrors([])
    setFormData(prev => {
      const existing = prev.crops.find(c => c.crop_type === cropType)
      if (existing) {
        return { ...prev, crops: prev.crops.filter(c => c.crop_type !== cropType) }
      }
      const products: CropProducts = {
        ...emptyCropProducts,
        corn_inputs: cropType === 'corn' ? { ...emptyCornInputs } : null,
        soybean_inputs: cropType === 'soybeans' ? { ...emptySoybeanInputs } : null,
        wheat_inputs: cropType === 'wheat' ? { ...emptyWheatInputs } : null,
        sugar_beet_inputs: cropType === 'sugar_beets' ? { ...emptySugarBeetInputs } : null,
        dry_bean_inputs: cropType === 'dry_beans' ? { ...emptyDryBeanInputs } : null,
      }
      return {
        ...prev,
        crops: [...prev.crops, { crop_type: cropType, acres: 0, current_products: products }],
      }
    })
  }

  const updateCropAcres = (cropType: CropType, acres: number) => {
    if (stepErrors.length > 0) setStepErrors([])
    setFormData(prev => ({
      ...prev,
      crops: prev.crops.map(c => c.crop_type === cropType ? { ...c, acres } : c),
    }))
  }

  const updateCropProducts = (cropIndex: number, products: CropProducts) => {
    if (stepErrors.length > 0) setStepErrors([])
    setFormData(prev => ({
      ...prev,
      crops: prev.crops.map((c, i) => i === cropIndex ? { ...c, current_products: products } : c),
    }))
  }

  if (loadingInputs) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-yi-green spinner mx-auto mb-4" />
          <p className="text-gray-600">Loading session inputs...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-dvh flex flex-col bg-white safe-top safe-bottom">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-100">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={handleBack}
            className="p-2 -ml-2 text-gray-600 btn-press"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-medium text-gray-900">
            {isEditMode ? 'Edit: ' : ''}{step?.title}
          </span>
          <div className="w-9" />
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-yi-green transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 p-6 animate-slide-in" key={step?.id}>
        {step?.id === 'farm' && (
          <FarmStep formData={formData} updateField={updateField} />
        )}
        {step?.id === 'crops' && (
          <CropsStep
            formData={formData}
            toggleCrop={toggleCrop}
            updateCropAcres={updateCropAcres}
          />
        )}
        {step?.id === 'equipment' && (
          <EquipmentStep
            formData={formData}
            updateField={updateField}
            toggleEquipment={toggleEquipment}
            updateWillingness={updateWillingness}
          />
        )}
        {currentCropIndex >= 0 && currentCropIndex < formData.crops.length && (
          <CurrentProductsStep
            crop={formData.crops[currentCropIndex]}
            onUpdate={(products) => updateCropProducts(currentCropIndex, products)}
          />
        )}
        {cornDetailsCropIndex >= 0 && cornDetailsCropIndex < formData.crops.length && (
          <CornDetailsStep
            crop={formData.crops[cornDetailsCropIndex]}
            equipment={formData.equipment}
            onUpdate={(cornInputs) => {
              updateCropProducts(cornDetailsCropIndex, {
                ...formData.crops[cornDetailsCropIndex].current_products,
                corn_inputs: cornInputs,
              })
            }}
          />
        )}
        {soybeanDetailsCropIndex >= 0 && soybeanDetailsCropIndex < formData.crops.length && (
          <SoybeanDetailsStep
            crop={formData.crops[soybeanDetailsCropIndex]}
            equipment={formData.equipment}
            onUpdate={(soybeanInputs) => {
              updateCropProducts(soybeanDetailsCropIndex, {
                ...formData.crops[soybeanDetailsCropIndex].current_products,
                soybean_inputs: soybeanInputs,
              })
            }}
          />
        )}
        {wheatDetailsCropIndex >= 0 && wheatDetailsCropIndex < formData.crops.length && (
          <WheatDetailsStep
            crop={formData.crops[wheatDetailsCropIndex]}
            onUpdate={(wheatInputs) => {
              updateCropProducts(wheatDetailsCropIndex, {
                ...formData.crops[wheatDetailsCropIndex].current_products,
                wheat_inputs: wheatInputs,
              })
            }}
          />
        )}
        {sugarBeetDetailsCropIndex >= 0 && sugarBeetDetailsCropIndex < formData.crops.length && (
          <SugarBeetDetailsStep
            crop={formData.crops[sugarBeetDetailsCropIndex]}
            equipment={formData.equipment}
            onUpdate={(sugarBeetInputs) => {
              updateCropProducts(sugarBeetDetailsCropIndex, {
                ...formData.crops[sugarBeetDetailsCropIndex].current_products,
                sugar_beet_inputs: sugarBeetInputs,
              })
            }}
          />
        )}
        {dryBeanDetailsCropIndex >= 0 && dryBeanDetailsCropIndex < formData.crops.length && (
          <DryBeanDetailsStep
            crop={formData.crops[dryBeanDetailsCropIndex]}
            onUpdate={(dryBeanInputs) => {
              updateCropProducts(dryBeanDetailsCropIndex, {
                ...formData.crops[dryBeanDetailsCropIndex].current_products,
                dry_bean_inputs: dryBeanInputs,
              })
            }}
          />
        )}
        {step?.id === 'soil' && (
          <SoilStep formData={formData} updateField={updateField} />
        )}
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 p-6 bg-gradient-to-t from-white via-white to-white/80 safe-bottom">
        {/* Validation errors */}
        {stepErrors.length > 0 && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-xl">
            {stepErrors.map((err, i) => (
              <p key={i} className="text-sm text-red-600">{err}</p>
            ))}
          </div>
        )}

        {/* Submit error from API */}
        {submitError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <button
          onClick={handleNext}
          disabled={isSubmitting}
          className="group w-full bg-yi-green hover:bg-yi-green-dark text-white
                     rounded-2xl p-4 font-semibold btn-press relative overflow-hidden
                     shadow-[0_8px_30px_-12px_rgba(26,71,42,0.4)]
                     hover:shadow-[0_12px_40px_-12px_rgba(26,71,42,0.5)]
                     transition-all duration-300
                     disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-yi-green"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />

          <div className="relative flex items-center justify-center gap-2">
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 spinner" />
                <span>Generating...</span>
              </>
            ) : isLastStep ? (
              <>
                <Check className="w-5 h-5" />
                <span>{isEditMode ? 'Re-run Recommendations' : 'Get Recommendations'}</span>
              </>
            ) : (
              <>
                <span>Continue</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </div>
        </button>
      </div>
    </div>
  )
}

// --- Step Components ---

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
]

function FarmStep({ formData, updateField }: {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
}) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Farm Name
        </label>
        <input
          type="text"
          value={formData.farm_name}
          onChange={e => updateField('farm_name', e.target.value)}
          placeholder="e.g., Smith Family Farm"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Farmer Name
        </label>
        <input
          type="text"
          value={formData.farmer_name}
          onChange={e => updateField('farmer_name', e.target.value)}
          placeholder="e.g., John Smith"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          State
        </label>
        <select
          value={formData.location_state || ''}
          onChange={e => updateField('location_state', e.target.value || null)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        >
          <option value="">Select state...</option>
          {US_STATES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Total Farm Acres
        </label>
        <input
          type="number"
          value={formData.total_acres ?? ''}
          onChange={e => updateField('total_acres', e.target.value ? parseInt(e.target.value) : null)}
          placeholder="e.g., 2000"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        />
      </div>
    </div>
  )
}

function CropsStep({ formData, toggleCrop, updateCropAcres }: {
  formData: FormData
  toggleCrop: (cropType: CropType) => void
  updateCropAcres: (cropType: CropType, acres: number) => void
}) {
  const allCrops: CropType[] = ['corn', 'soybeans', 'wheat', 'alfalfa', 'potatoes', 'sugar_beets', 'dry_beans']
  const selectedCropTypes = new Set(formData.crops.map(c => c.crop_type))

  return (
    <div className="space-y-6">
      <p className="text-gray-600">Select all crops and enter acres for each</p>

      <div className="space-y-3">
        {allCrops.map(cropType => {
          const isSelected = selectedCropTypes.has(cropType)
          const cropEntry = formData.crops.find(c => c.crop_type === cropType)

          return (
            <div key={cropType}>
              <button
                onClick={() => toggleCrop(cropType)}
                className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-4 btn-press
                  ${isSelected ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
              >
                <span className="text-2xl">{cropEmojis[cropType]}</span>
                <span className="font-medium text-gray-900 flex-1">{cropLabels[cropType]}</span>
                {isSelected && <Check className="w-5 h-5 text-yi-green" />}
              </button>

              {isSelected && cropEntry && (
                <div className="mt-2 ml-12 mr-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      value={cropEntry.acres || ''}
                      onChange={e => updateCropAcres(cropType, parseInt(e.target.value) || 0)}
                      placeholder="Acres"
                      className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm
                                 focus:border-yi-green focus:ring-1 focus:ring-yi-green"
                    />
                    <span className="text-sm text-gray-500">acres</span>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function YesNoButtons({ value, onChange }: {
  value: boolean | null
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`flex-1 p-2.5 rounded-lg border text-sm font-medium transition-colors
          ${value === true
            ? 'border-yi-green bg-yi-green/5 text-yi-green'
            : 'border-gray-200 text-gray-700 hover:border-gray-300'
          }`}
      >Yes</button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`flex-1 p-2.5 rounded-lg border text-sm font-medium transition-colors
          ${value === false
            ? 'border-amber-300 bg-amber-50 text-amber-700'
            : 'border-gray-200 text-gray-700 hover:border-gray-300'
          }`}
      >No</button>
    </div>
  )
}

function EquipmentStep({ formData, updateField, toggleEquipment, updateWillingness }: {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  toggleEquipment: (equip: EquipmentType) => void
  updateWillingness: (
    field: 'in_furrow_willingness' | 'two_by_two_willingness',
    equip: EquipmentType,
    update: Partial<EquipmentWillingness>,
  ) => void
}) {
  const { kamariMode } = useFeatureFlags()
  const willingnessItems: { equip: EquipmentType; label: string; field: 'in_furrow_willingness' | 'two_by_two_willingness' }[] = [
    { equip: 'in_furrow', label: 'In-Furrow', field: 'in_furrow_willingness' },
    { equip: 'two_by_two', label: '2x2 / 2x2x2 / 2x0 / 2x2x0', field: 'two_by_two_willingness' },
  ]

  const sprayerOptions: { value: SprayerOwnership; label: string }[] = [
    { value: 'owns', label: 'Own sprayer' },
    { value: 'neighbor_friend', label: 'Neighbor / Friend' },
    { value: 'third_party_retailer', label: 'Third-party retailer' },
  ]

  const hasSidedress = formData.equipment.includes('sidedress')
  const hasSprayer = formData.equipment.includes('sprayer')

  return (
    <div className="space-y-8">
      <div>
        <p className="font-medium text-gray-900 mb-3">Planter Equipment</p>
        <p className="text-sm text-gray-500 mb-4">Tell us about the farmer's planter capabilities</p>
        <div className="space-y-3">
          {willingnessItems.map(({ equip, label, field }) => {
            const w = formData[field]
            const isIncluded = formData.equipment.includes(equip)

            return (
              <div
                key={equip}
                className={`p-4 rounded-xl border-2 transition-colors
                  ${isIncluded ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
              >
                <p className="font-medium text-gray-900 mb-3">{label}</p>

                {/* Step 1: Has it? */}
                <div>
                  <p className="text-sm text-gray-600 mb-2">Does the farmer have {label.toLowerCase()} capability?</p>
                  <YesNoButtons value={w.has} onChange={v => updateWillingness(field, equip, { has: v })} />
                </div>

                {/* Step 2: Uses it? */}
                {w.has === true && (
                  <div className="mt-3 ml-3 pl-3 border-l-2 border-gray-200">
                    <p className="text-sm text-gray-600 mb-2">Do they currently use it?</p>
                    <YesNoButtons value={w.uses} onChange={v => updateWillingness(field, equip, { uses: v })} />

                    {/* Step 3: Willing? */}
                    {w.uses === false && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600 mb-2">Would they be willing to use it?</p>
                        <YesNoButtons value={w.willing} onChange={v => updateWillingness(field, equip, { willing: v })} />
                        {w.willing === false && (
                          <p className="text-xs text-amber-600 mt-2">
                            No {label.toLowerCase()} products will be recommended.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {w.has === false && (
                  <p className="text-xs text-gray-400 mt-2">
                    No {label.toLowerCase()} products will be recommended.
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div>
        <p className="font-medium text-gray-900 mb-3">Other Equipment</p>
        <p className="text-sm text-gray-500 mb-4">Select all that apply</p>
        <div className="space-y-2">
          {([
            { value: 'sidedress' as EquipmentType, label: 'Sidedress' },
            { value: 'sprayer' as EquipmentType, label: 'Sprayer' },
          ]).map(equip => (
            <div key={equip.value}>
              <label
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer btn-press
                  ${formData.equipment.includes(equip.value)
                    ? 'border-yi-green bg-yi-green/5'
                    : 'border-gray-200'
                  }`}
              >
                <input
                  type="checkbox"
                  checked={formData.equipment.includes(equip.value)}
                  onChange={() => toggleEquipment(equip.value)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center
                  ${formData.equipment.includes(equip.value)
                    ? 'bg-yi-green border-yi-green'
                    : 'border-gray-300'
                  }`}
                >
                  {formData.equipment.includes(equip.value) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="font-medium text-gray-900">{equip.label}</span>
              </label>

              {/* Y-Drop sub-checkbox when sidedress is selected */}
              {equip.value === 'sidedress' && hasSidedress && (
                <label
                  className={`flex items-center gap-3 p-3 ml-8 mt-1 rounded-lg border cursor-pointer
                    ${formData.has_y_drop_attachment
                      ? 'border-yi-green bg-yi-green/5'
                      : 'border-gray-200'
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.has_y_drop_attachment}
                    onChange={() => updateField('has_y_drop_attachment', !formData.has_y_drop_attachment)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center
                    ${formData.has_y_drop_attachment
                      ? 'bg-yi-green border-yi-green'
                      : 'border-gray-300'
                    }`}
                  >
                    {formData.has_y_drop_attachment && (
                      <Check className="w-2.5 h-2.5 text-white" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700">Y-Drop Attachment</span>
                </label>
              )}

              {/* Spray method & water volume when sprayer is selected */}
              {equip.value === 'sprayer' && hasSprayer && (
                <div className="ml-8 mt-2 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Spray Method</p>
                    <div className="flex gap-2">
                      {([
                        { value: 'ground' as SprayMethod, label: 'Ground Rig' },
                        { value: 'aerial' as SprayMethod, label: 'Aerial / Drone' },
                      ]).map(opt => (
                        <label
                          key={opt.value}
                          className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border cursor-pointer text-sm font-medium
                            ${formData.spray_method === opt.value
                              ? 'border-yi-green bg-yi-green/5 text-yi-green'
                              : 'border-gray-200 text-gray-700'
                            }`}
                        >
                          <input
                            type="radio"
                            name="spray_method"
                            checked={formData.spray_method === opt.value}
                            onChange={() => {
                              updateField('spray_method', opt.value)
                              updateField('spray_water_volume_gal', null)
                            }}
                            className="sr-only"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Water Volume (gal/A)
                      <span className="font-normal text-gray-400 ml-1">
                        {formData.spray_method === 'ground' ? '(typically 10-20)' : '(typically 2-3)'}
                      </span>
                    </p>
                    {formData.spray_method === 'aerial' ? (
                      <div className="flex gap-2">
                        {[2, 3].map(val => (
                          <label
                            key={val}
                            className={`flex-1 flex items-center justify-center p-3 rounded-lg border cursor-pointer text-sm font-medium
                              ${formData.spray_water_volume_gal === val
                                ? 'border-yi-green bg-yi-green/5 text-yi-green'
                                : 'border-gray-200 text-gray-700'
                              }`}
                          >
                            <input
                              type="radio"
                              name="spray_water_volume"
                              checked={formData.spray_water_volume_gal === val}
                              onChange={() => updateField('spray_water_volume_gal', val)}
                              className="sr-only"
                            />
                            {val} gal/A
                          </label>
                        ))}
                      </div>
                    ) : (
                      <input
                        type="number"
                        min={5}
                        max={30}
                        step={1}
                        value={formData.spray_water_volume_gal ?? ''}
                        onChange={e => updateField('spray_water_volume_gal', e.target.value ? Number(e.target.value) : null)}
                        placeholder="e.g. 15"
                        className="w-full p-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-yi-green"
                      />
                    )}
                  </div>
                  {/* Water pH — kamari mode only */}
                  {kamariMode && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Water pH
                        <span className="font-normal text-gray-400 ml-1">(if tested)</span>
                      </p>
                      <input
                        type="number"
                        min={1}
                        max={14}
                        step={0.1}
                        value={formData.water_ph ?? ''}
                        onChange={e => updateField('water_ph', e.target.value ? Number(e.target.value) : null)}
                        placeholder="e.g. 7.2"
                        className="w-full p-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-yi-green"
                      />
                      <p className="text-xs text-gray-400 mt-1">Affects Nano pHlow rate calculation</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="font-medium text-gray-900 mb-3">Sprayer Access</p>
        <div className="space-y-2">
          {sprayerOptions.map(option => (
            <label
              key={option.value}
              className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer btn-press
                ${formData.sprayer_ownership === option.value
                  ? 'border-yi-green bg-yi-green/5'
                  : 'border-gray-200'
                }`}
            >
              <input
                type="radio"
                name="sprayer"
                checked={formData.sprayer_ownership === option.value}
                onChange={() => updateField('sprayer_ownership', option.value)}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${formData.sprayer_ownership === option.value
                  ? 'border-yi-green'
                  : 'border-gray-300'
                }`}
              >
                {formData.sprayer_ownership === option.value && (
                  <div className="w-2.5 h-2.5 rounded-full bg-yi-green" />
                )}
              </div>
              <span className="font-medium text-gray-900">{option.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}

function CurrentProductsStep({ crop, onUpdate }: {
  crop: CropEntry
  onUpdate: (products: CropProducts) => void
}) {
  const cp = crop.current_products

  const addProduct = (slot: 'in_furrow' | 'two_by_two' | 'sidedress') => {
    onUpdate({
      ...cp,
      [slot]: [...cp[slot], { product: '', rate: 0, unit: 'gallons' as const }],
    })
  }

  const removeProduct = (slot: 'in_furrow' | 'two_by_two' | 'sidedress', index: number) => {
    onUpdate({
      ...cp,
      [slot]: cp[slot].filter((_, i) => i !== index),
    })
  }

  const updateProduct = (
    slot: 'in_furrow' | 'two_by_two' | 'sidedress',
    index: number,
    field: keyof CurrentProduct,
    value: string | number,
  ) => {
    onUpdate({
      ...cp,
      [slot]: cp[slot].map((p, i) => i === index ? { ...p, [field]: value } : p),
    })
  }

  const isWheat = crop.crop_type === 'wheat'

  const slotLabels = {
    in_furrow: 'In-Furrow Products',
    two_by_two: '2x2 / 2x2x2 / 2x0 / 2x2x0 Products',
    sidedress: isWheat ? 'Top Dress Products' : 'Sidedress Products',
  }

  const slotPlaceholders = {
    in_furrow: '10-34-0',
    two_by_two: '28-0-0, 10-34-0, ATS, EDTA Zinc',
    sidedress: isWheat ? 'UAN, AMS' : 'UAN, ATS',
  }

  // Corn-specific product dropdown options per slot
  const cornProductOptions: Record<'in_furrow' | 'two_by_two' | 'sidedress', string[]> = {
    in_furrow: ['10-34-0', 'Zinc'],
    two_by_two: ['10-34-0', 'ATS', '28% UAN', '32% UAN', 'Zinc', 'Manganese', 'Boron'],
    sidedress: ['28% UAN', '32% UAN', 'ATS', 'Zinc', 'Manganese', 'Boron'],
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-2xl">{cropEmojis[crop.crop_type]}</span>
        <div>
          <p className="font-medium text-gray-900">
            {cropLabels[crop.crop_type]} &mdash; {crop.acres} acres
          </p>
          <p className="text-sm text-gray-500">What products are you currently using?</p>
        </div>
      </div>

      {/* Product slots — hide sidedress for soybeans; hide in-furrow & 2x2 for wheat */}
      {(['in_furrow', 'two_by_two', 'sidedress'] as const)
        .filter(slot => !(slot === 'sidedress' && crop.crop_type === 'soybeans'))
        .filter(slot => !(isWheat && (slot === 'in_furrow' || slot === 'two_by_two')))
        .map(slot => (
        <div key={slot}>
          <p className="text-sm font-medium text-gray-700 mb-2">{slotLabels[slot]}</p>
          <div className="space-y-2">
            {cp[slot].map((product, idx) => {
              const isCorn = crop.crop_type === 'corn'
              const options = cornProductOptions[slot]
              const isCustom = isCorn && product.product !== '' && !options.includes(product.product)

              return (
              <div key={idx} className="flex items-center gap-2">
                {isCorn ? (
                  <div className="flex-1 flex gap-2">
                    <select
                      value={isCustom ? '__other__' : product.product}
                      onChange={e => {
                        const val = e.target.value
                        if (val === '__other__') {
                          updateProduct(slot, idx, 'product', ' ')
                        } else {
                          updateProduct(slot, idx, 'product', val)
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm
                                 focus:border-yi-green focus:ring-1 focus:ring-yi-green bg-white"
                    >
                      <option value="">Select product…</option>
                      {options.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                      <option value="__other__">Other…</option>
                    </select>
                    {isCustom && (
                      <input
                        type="text"
                        value={product.product.trim()}
                        onChange={e => updateProduct(slot, idx, 'product', e.target.value || ' ')}
                        placeholder="Product name"
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm
                                   focus:border-yi-green focus:ring-1 focus:ring-yi-green"
                        autoFocus
                      />
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={product.product}
                    onChange={e => updateProduct(slot, idx, 'product', e.target.value)}
                    placeholder={slotPlaceholders[slot]}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm
                               focus:border-yi-green focus:ring-1 focus:ring-yi-green"
                  />
                )}
                <input
                  type="number"
                  value={product.rate || ''}
                  onChange={e => updateProduct(slot, idx, 'rate', parseFloat(e.target.value) || 0)}
                  placeholder="Rate"
                  className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-sm
                             focus:border-yi-green focus:ring-1 focus:ring-yi-green"
                />
                <select
                  value={product.unit}
                  onChange={e => updateProduct(slot, idx, 'unit', e.target.value)}
                  className="px-2 py-2 border border-gray-200 rounded-lg text-sm
                             focus:border-yi-green focus:ring-1 focus:ring-yi-green"
                >
                  <option value="gallons">gal</option>
                  <option value="quarts">qt</option>
                  <option value="ounces">oz</option>
                </select>
                <button
                  onClick={() => removeProduct(slot, idx)}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              )
            })}
            <button
              onClick={() => addProduct(slot)}
              className="flex items-center gap-1 text-sm text-yi-green font-medium hover:text-yi-green-dark"
            >
              <Plus className="w-4 h-4" /> Add product
            </button>
          </div>
        </div>
      ))}

      {/* Toggle questions */}
      <div className="space-y-3 pt-2 border-t border-gray-100">
        {!isWheat && (
          <>
            <ToggleRow
              label="Uses Xyway?"
              checked={cp.uses_xyway}
              onChange={v => onUpdate({ ...cp, uses_xyway: v, xyway_rate: v ? cp.xyway_rate : null })}
            />
            {cp.uses_xyway && (
              <div className="ml-8 flex items-center gap-2">
                <input
                  type="number"
                  step="0.1"
                  value={cp.xyway_rate ?? ''}
                  onChange={e => onUpdate({ ...cp, xyway_rate: parseFloat(e.target.value) || null })}
                  placeholder="Rate"
                  className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm
                             focus:border-yi-green focus:ring-1 focus:ring-yi-green"
                />
                <span className="text-sm text-gray-500">fl oz/A</span>
              </div>
            )}
          </>
        )}

        <ToggleRow
          label={isWheat
            ? 'Making any foliar application during the growing season?'
            : 'Making any foliar application from V3 to Pre-Tassel?'}
          checked={cp.vegetative_fungicide}
          onChange={v => onUpdate({ ...cp, vegetative_fungicide: v })}
        />

        <ToggleRow
          label="Reproductive fungicide pass?"
          checked={cp.reproductive_fungicide}
          onChange={v => onUpdate({ ...cp, reproductive_fungicide: v })}
        />

        {(crop.crop_type === 'corn' || crop.crop_type === 'soybeans') && (
          <>
            <ToggleRow
              label="Yield-limiting deer damage along wooded areas?"
              checked={cp.deer_damage}
              onChange={v => onUpdate({ ...cp, deer_damage: v, deer_damage_pct_acres: v ? cp.deer_damage_pct_acres : null })}
            />
            {cp.deer_damage && (
              <div className="ml-8 flex items-center gap-2">
                <input
                  type="number"
                  step="1"
                  min="1"
                  max="100"
                  value={cp.deer_damage_pct_acres ?? ''}
                  onChange={e => onUpdate({ ...cp, deer_damage_pct_acres: parseFloat(e.target.value) || null })}
                  placeholder="% of acres"
                  className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm
                             focus:border-yi-green focus:ring-1 focus:ring-yi-green"
                />
                <span className="text-sm text-gray-500">% of acres affected</span>
              </div>
            )}
          </>
        )}

        <ToggleRow
          label="Weed escapes that cause you to respray often?"
          checked={cp.weed_escapes}
          onChange={v => onUpdate({ ...cp, weed_escapes: v, weed_escape_acres: v ? cp.weed_escape_acres : null })}
        />
        {cp.weed_escapes && (
          <div className="ml-8 flex items-center gap-2">
            <input
              type="number"
              step="1"
              value={cp.weed_escape_acres ?? ''}
              onChange={e => onUpdate({ ...cp, weed_escape_acres: parseFloat(e.target.value) || null })}
              placeholder="Optional"
              className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm
                         focus:border-yi-green focus:ring-1 focus:ring-yi-green"
            />
            <span className="text-sm text-gray-500">acres (optional)</span>
          </div>
        )}
      </div>
    </div>
  )
}

function CornDetailsStep({ crop, equipment, onUpdate }: {
  crop: CropEntry
  equipment: EquipmentType[]
  onUpdate: (cornInputs: CornInputs) => void
}) {
  const ci = crop.current_products.corn_inputs || { ...emptyCornInputs }
  const hasSidedress = equipment.includes('sidedress')
  const isUAN = ci.nitrogen_form === 'uan'

  const update = (partial: Partial<CornInputs>) => {
    onUpdate({ ...ci, ...partial })
  }

  return (
    <div className="space-y-6">
      <p className="text-gray-600">
        These details help us build a precise corn program based on your operation.
      </p>

      {/* APH + Yield Goal */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            APH (bu/A)
          </label>
          <input
            type="number"
            value={ci.aph ?? ''}
            onChange={e => update({ aph: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="e.g., 200"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
          <p className="text-xs text-gray-400 mt-1">Actual production history</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Yield Goal (bu/A)
          </label>
          <input
            type="number"
            value={ci.yield_goal ?? ''}
            onChange={e => update({ yield_goal: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="e.g., 220"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
          <p className="text-xs text-gray-400 mt-1">Drives product selection and rates</p>
        </div>
      </div>

      {/* Nitrogen Form */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Nitrogen Form</p>
        <div className="space-y-2">
          {([
            { value: 'uan' as const, label: 'UAN (28% or 32%)' },
            { value: 'anhydrous' as const, label: 'Anhydrous Ammonia (82%)' },
          ]).map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer btn-press
                ${ci.nitrogen_form === opt.value ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="nitrogen_form"
                checked={ci.nitrogen_form === opt.value}
                onChange={() => update({ nitrogen_form: opt.value })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${ci.nitrogen_form === opt.value ? 'border-yi-green' : 'border-gray-300'}`}
              >
                {ci.nitrogen_form === opt.value && (
                  <div className="w-2.5 h-2.5 rounded-full bg-yi-green" />
                )}
              </div>
              <span className="font-medium text-gray-900 text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* UAN gallons — conditional on UAN selected */}
      {isUAN && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              UAN gal/A in 2x2
            </label>
            <input
              type="number"
              step="0.5"
              value={ci.uan_gallons_2x2 ?? ''}
              onChange={e => update({ uan_gallons_2x2: e.target.value ? parseFloat(e.target.value) : null })}
              placeholder="e.g., 10"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl
                         focus:border-yi-green focus:ring-1 focus:ring-yi-green"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              UAN gal/A at sidedress
            </label>
            <input
              type="number"
              step="0.5"
              value={ci.uan_gallons_sidedress ?? ''}
              onChange={e => update({ uan_gallons_sidedress: e.target.value ? parseFloat(e.target.value) : null })}
              placeholder="e.g., 20"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl
                         focus:border-yi-green focus:ring-1 focus:ring-yi-green"
            />
          </div>
        </div>
      )}

      {/* Willing to sidedress — conditional on having sidedress equipment */}
      {hasSidedress && (
        <ToggleRow
          label="Willing to add nutrients at sidedress?"
          checked={ci.willing_to_sidedress}
          onChange={v => update({ willing_to_sidedress: v })}
        />
      )}

      {/* Sulfur already purchased */}
      <ToggleRow
        label="Already purchased sulfur in UAN?"
        checked={ci.sulfur_already_purchased}
        onChange={v => update({ sulfur_already_purchased: v })}
      />

      {/* Uses manure */}
      <ToggleRow
        label="Uses manure / high phosphorus history?"
        checked={ci.uses_manure}
        onChange={v => update({ uses_manure: v })}
      />

      {/* Fungicide plan */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Fungicide Plan (VT-R3)</p>
        <div className="space-y-2">
          {([
            { value: 'vt_r3_ground' as const, label: 'Ground Application' },
            { value: 'vt_r3_aerial' as const, label: 'Aerial / Drone' },
            { value: 'none' as const, label: 'No Fungicide Planned' },
          ]).map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer btn-press
                ${ci.fungicide_plan === opt.value ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="fungicide_plan"
                checked={ci.fungicide_plan === opt.value}
                onChange={() => update({ fungicide_plan: opt.value })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${ci.fungicide_plan === opt.value ? 'border-yi-green' : 'border-gray-300'}`}
              >
                {ci.fungicide_plan === opt.value && (
                  <div className="w-2.5 h-2.5 rounded-full bg-yi-green" />
                )}
              </div>
              <span className="font-medium text-gray-900 text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Water volume — foliar herbicide pass */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Foliar/Herbicide Pass — Water (gal/A)
        </label>
        <input
          type="number"
          step="1"
          value={ci.foliar_water_volume_gal ?? ''}
          onChange={e => update({ foliar_water_volume_gal: e.target.value ? parseFloat(e.target.value) : null })}
          placeholder="e.g., 15"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        />
        <p className="text-xs text-gray-400 mt-1">How many gallons of water per acre for foliar spraying?</p>
      </div>

      {/* Water volume — fungicide pass (only if ground fungicide planned) */}
      {ci.fungicide_plan === 'vt_r3_ground' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            VT-R3 Fungicide Pass — Water (gal/A)
          </label>
          <input
            type="number"
            step="1"
            value={ci.fungicide_water_volume_gal ?? ''}
            onChange={e => update({ fungicide_water_volume_gal: e.target.value ? parseFloat(e.target.value) : null })}
            placeholder="e.g., 15"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
          <p className="text-xs text-gray-400 mt-1">How many gallons of water per acre for the fungicide pass?</p>
        </div>
      )}

      {/* Drone water volume (aerial selected — default 3, let them adjust to 2) */}
      {ci.fungicide_plan === 'vt_r3_aerial' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Drone Fungicide Pass — Water (gal/A)
          </label>
          <div className="flex gap-3">
            {[2, 3].map(v => (
              <label
                key={v}
                className={`flex-1 flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer btn-press text-sm
                  ${(ci.fungicide_water_volume_gal ?? 3) === v ? 'border-yi-green bg-yi-green/5 font-semibold' : 'border-gray-200'}`}
              >
                <input
                  type="radio"
                  name="drone_water"
                  checked={(ci.fungicide_water_volume_gal ?? 3) === v}
                  onChange={() => update({ fungicide_water_volume_gal: v })}
                  className="sr-only"
                />
                {v} gal/A
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function WheatDetailsStep({ crop, onUpdate }: {
  crop: CropEntry
  onUpdate: (wheatInputs: WheatInputs) => void
}) {
  const wi = crop.current_products.wheat_inputs || { ...emptyWheatInputs }

  const update = (partial: Partial<WheatInputs>) => {
    onUpdate({ ...wi, ...partial })
  }

  return (
    <div className="space-y-6">
      <p className="text-gray-600">
        These details help us build a precise wheat program based on your operation.
      </p>

      {/* Yield Goal */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Yield Goal (bu/A)
        </label>
        <input
          type="number"
          value={wi.yield_goal ?? ''}
          onChange={e => update({ yield_goal: e.target.value ? parseInt(e.target.value) : null })}
          placeholder="e.g., 110"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        />
        <p className="text-xs text-gray-400 mt-1">Tiers: 80-100 (standard), 100-120 (medium), &gt;120 (high)</p>
      </div>

      {/* Nitrogen Passes */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Nitrogen Passes</p>
        <div className="space-y-2">
          {([
            { value: 1 as const, label: '1 pass — single spring application' },
            { value: 2 as const, label: '2 passes — green-up + Feekes 5-6' },
          ]).map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer btn-press
                ${wi.nitrogen_passes === opt.value ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="nitrogen_passes"
                checked={wi.nitrogen_passes === opt.value}
                onChange={() => update({ nitrogen_passes: opt.value })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${wi.nitrogen_passes === opt.value ? 'border-yi-green' : 'border-gray-300'}`}
              >
                {wi.nitrogen_passes === opt.value && (
                  <div className="w-2.5 h-2.5 rounded-full bg-yi-green" />
                )}
              </div>
              <span className="font-medium text-gray-900 text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Wild Onion/Garlic */}
      <ToggleRow
        label="Wild onion / garlic present?"
        checked={wi.wild_onion_garlic}
        onChange={v => update({ wild_onion_garlic: v })}
      />
    </div>
  )
}

function SugarBeetDetailsStep({ crop, equipment, onUpdate }: {
  crop: CropEntry
  equipment: EquipmentType[]
  onUpdate: (sugarBeetInputs: SugarBeetInputs) => void
}) {
  const sb = crop.current_products.sugar_beet_inputs || { ...emptySugarBeetInputs }
  const hasInFurrowEquipment = equipment.includes('in_furrow')
  const has2x2Equipment = equipment.includes('two_by_two')
  const hasSidedressEquipment = equipment.includes('sidedress')

  const update = (partial: Partial<SugarBeetInputs>) => {
    onUpdate({ ...sb, ...partial })
  }

  return (
    <div className="space-y-6">
      <p className="text-gray-600">
        These details help us build a precise sugar beet program based on your operation.
      </p>

      {/* Yield Level */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Yield Level (tons/A)</p>
        <div className="space-y-2">
          {([
            { value: '26-30' as SugarBeetYieldLevel, label: '26-30 tons/A (standard)' },
            { value: '30-40' as SugarBeetYieldLevel, label: '30-40 tons/A (high)' },
          ]).map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer btn-press
                ${sb.yield_level === opt.value ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="sb_yield_level"
                checked={sb.yield_level === opt.value}
                onChange={() => update({ yield_level: opt.value })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${sb.yield_level === opt.value ? 'border-yi-green' : 'border-gray-300'}`}
              >
                {sb.yield_level === opt.value && (
                  <div className="w-2.5 h-2.5 rounded-full bg-yi-green" />
                )}
              </div>
              <span className="font-medium text-gray-900 text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* In-furrow */}
      {hasInFurrowEquipment && (
        <ToggleRow
          label="In-furrow on sugar beet planter?"
          checked={sb.has_in_furrow}
          onChange={v => update({ has_in_furrow: v })}
        />
      )}

      {/* 2x2 */}
      {has2x2Equipment && (
        <>
          <ToggleRow
            label="2x2 placement on sugar beet planter?"
            checked={sb.has_2x2}
            onChange={v => update({ has_2x2: v })}
          />

          {sb.has_2x2 && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2x2 UAN rate (gal/A)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={sb.current_2x2_uan_rate ?? ''}
                  onChange={e => update({ current_2x2_uan_rate: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="e.g., 8"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl
                             focus:border-yi-green focus:ring-1 focus:ring-yi-green"
                />
                <p className="text-xs text-gray-400 mt-1">28-0-0 rate</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2x2 10-34-0 rate (gal/A)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={sb.current_2x2_1034_rate ?? ''}
                  onChange={e => update({ current_2x2_1034_rate: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="e.g., 10"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl
                             focus:border-yi-green focus:ring-1 focus:ring-yi-green"
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Sidedress */}
      {hasSidedressEquipment && (
        <>
          <ToggleRow
            label="Sidedress applications?"
            checked={sb.sidedresses}
            onChange={v => update({ sidedresses: v })}
          />

          {sb.sidedresses && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sidedress UAN (gal/A)
              </label>
              <input
                type="number"
                step="0.5"
                value={sb.sidedress_uan_gallons ?? ''}
                onChange={e => update({ sidedress_uan_gallons: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="e.g., 20"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl
                           focus:border-yi-green focus:ring-1 focus:ring-yi-green"
              />
            </div>
          )}
        </>
      )}

      {/* CLS Spray Passes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          CLS Fungicide Spray Passes
        </label>
        <input
          type="number"
          min={6}
          max={10}
          value={sb.cls_spray_count}
          onChange={e => update({ cls_spray_count: Math.max(6, Math.min(10, parseInt(e.target.value) || 6)) })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        />
        <p className="text-xs text-gray-400 mt-1">Typically 6-10 passes</p>
      </div>

      {/* Water Carrier Volume */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Water Carrier Volume (gal/A)
        </label>
        <input
          type="number"
          step="0.5"
          value={sb.cls_water_carrier_gallons ?? ''}
          onChange={e => update({ cls_water_carrier_gallons: e.target.value ? parseFloat(e.target.value) : null })}
          placeholder="e.g., 15"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        />
        <p className="text-xs text-gray-400 mt-1">For Nano pHlow rate calculation</p>
      </div>
    </div>
  )
}

function SoybeanDetailsStep({ crop, equipment, onUpdate }: {
  crop: CropEntry
  equipment: EquipmentType[]
  onUpdate: (soybeanInputs: SoybeanInputs) => void
}) {
  const si = crop.current_products.soybean_inputs || { ...emptySoybeanInputs }
  const hasInFurrowEquipment = equipment.includes('in_furrow')

  const update = (partial: Partial<SoybeanInputs>) => {
    onUpdate({ ...si, ...partial })
  }

  const rowSpacingOptions: { value: RowSpacing; label: string }[] = [
    { value: 'drilled', label: 'Drilled (7.5")' },
    { value: '15', label: '15"' },
    { value: '20', label: '20"' },
    { value: '30', label: '30"' },
    { value: '40', label: '40"' },
    { value: 'twin', label: 'Twin Row' },
  ]

  const scnOptions: { value: SCNHistory; label: string }[] = [
    { value: 'yes', label: 'Yes — confirmed SCN history' },
    { value: 'no', label: 'No SCN detected' },
    { value: 'unknown', label: 'Unknown / not tested' },
  ]

  return (
    <div className="space-y-6">
      <p className="text-gray-600">
        These details help us build a precise soybean program based on your operation.
      </p>

      {/* In-furrow planter — FIRST question per Ryan's feedback */}
      {hasInFurrowEquipment && (
        <ToggleRow
          label="In-furrow planter on soybean planter?"
          checked={si.has_in_furrow_planter}
          onChange={v => update({
            has_in_furrow_planter: v,
            // Reset downstream if turning off
            ...(!v ? { currently_uses_infurrow_fertilizer: false, wants_npk_starter: false } : {}),
          })}
        />
      )}

      {/* APH + Yield Goal */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            APH (bu/A)
          </label>
          <input
            type="number"
            value={si.aph ?? ''}
            onChange={e => update({ aph: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="e.g., 55"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
          <p className="text-xs text-gray-400 mt-1">Actual production history</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Yield Goal (bu/A)
          </label>
          <input
            type="number"
            value={si.yield_goal ?? ''}
            onChange={e => update({ yield_goal: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="e.g., 65"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
          <p className="text-xs text-gray-400 mt-1">Drives product selection and rates</p>
        </div>
      </div>

      {/* Row Spacing */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Row Spacing</p>
        <div className="grid grid-cols-3 gap-2">
          {rowSpacingOptions.map(opt => (
            <label
              key={opt.value}
              className={`flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer btn-press text-sm
                ${si.row_spacing === opt.value ? 'border-yi-green bg-yi-green/5 font-semibold' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="row_spacing"
                checked={si.row_spacing === opt.value}
                onChange={() => update({ row_spacing: opt.value })}
                className="sr-only"
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* SCN History */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Soybean Cyst Nematode (SCN) History</p>
        <div className="space-y-2">
          {scnOptions.map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer btn-press
                ${si.scn_history === opt.value ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="scn_history"
                checked={si.scn_history === opt.value}
                onChange={() => update({ scn_history: opt.value })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${si.scn_history === opt.value ? 'border-yi-green' : 'border-gray-300'}`}
              >
                {si.scn_history === opt.value && (
                  <div className="w-2.5 h-2.5 rounded-full bg-yi-green" />
                )}
              </div>
              <span className="font-medium text-gray-900 text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* SCN seed treatment — conditional on SCN yes/unknown */}
      {(si.scn_history === 'yes' || si.scn_history === 'unknown') && (
        <ToggleRow
          label="Using SCN seed treatment?"
          checked={si.using_scn_seed_treatment}
          onChange={v => update({ using_scn_seed_treatment: v })}
        />
      )}

      {/* Currently uses in-furrow fertilizer */}
      {si.has_in_furrow_planter && (
        <>
          <ToggleRow
            label="Currently using in-furrow fertilizer (e.g., 10-34-0)?"
            checked={si.currently_uses_infurrow_fertilizer}
            onChange={v => update({ currently_uses_infurrow_fertilizer: v, wants_npk_starter: v ? si.wants_npk_starter : false })}
          />

          {/* NPK starter preference */}
          {si.currently_uses_infurrow_fertilizer && (
            <ToggleRow
              label="Want NPK starter (Vitaliz Pure) instead of standard bundle?"
              checked={si.wants_npk_starter}
              onChange={v => update({ wants_npk_starter: v })}
            />
          )}
        </>
      )}

      {/* Foliar herbicide pass — water volume */}
      <div className="pt-2 border-t border-gray-100">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Post Herbicide/Foliar Pass — Water (gal/A)
        </label>
        <input
          type="number"
          step="1"
          value={si.foliar_herbicide_water_gal ?? ''}
          onChange={e => update({ foliar_herbicide_water_gal: e.target.value ? parseFloat(e.target.value) : null })}
          placeholder="e.g., 15"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        />
        <p className="text-xs text-gray-400 mt-1">How many gallons of water per acre for foliar herbicide pass?</p>
      </div>

      {/* R3 fungicide pass — application method */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">R3 Fungicide Pass — Application Method</p>
        <div className="space-y-2">
          {([
            { value: 'ground' as const, label: 'Ground Rig' },
            { value: 'aerial' as const, label: 'Drone / Aerial (Airplane, Helicopter)' },
          ]).map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer btn-press
                ${si.r3_application_method === opt.value ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="r3_application_method"
                checked={si.r3_application_method === opt.value}
                onChange={() => update({ r3_application_method: opt.value, r3_water_volume_gal: opt.value === 'aerial' ? 3 : si.r3_water_volume_gal })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${si.r3_application_method === opt.value ? 'border-yi-green' : 'border-gray-300'}`}
              >
                {si.r3_application_method === opt.value && (
                  <div className="w-2.5 h-2.5 rounded-full bg-yi-green" />
                )}
              </div>
              <span className="font-medium text-gray-900 text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* R3 water volume — conditional on application method */}
      {si.r3_application_method === 'ground' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            R3 Fungicide Pass — Water (gal/A)
          </label>
          <input
            type="number"
            step="1"
            value={si.r3_water_volume_gal ?? ''}
            onChange={e => update({ r3_water_volume_gal: e.target.value ? parseFloat(e.target.value) : null })}
            placeholder="e.g., 15"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
          <p className="text-xs text-gray-400 mt-1">Ground rig water volume for fungicide application</p>
        </div>
      )}

      {si.r3_application_method === 'aerial' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            R3 Drone/Aerial — Water (gal/A)
          </label>
          <div className="flex gap-3">
            {[2, 3].map(v => (
              <label
                key={v}
                className={`flex-1 flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer btn-press text-sm
                  ${(si.r3_water_volume_gal ?? 3) === v ? 'border-yi-green bg-yi-green/5 font-semibold' : 'border-gray-200'}`}
              >
                <input
                  type="radio"
                  name="r3_drone_water"
                  checked={(si.r3_water_volume_gal ?? 3) === v}
                  onChange={() => update({ r3_water_volume_gal: v })}
                  className="sr-only"
                />
                {v} gal/A
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function DryBeanDetailsStep({ crop, onUpdate }: {
  crop: CropEntry
  onUpdate: (dryBeanInputs: DryBeanInputs) => void
}) {
  const db = crop.current_products.dry_bean_inputs || { ...emptyDryBeanInputs }

  const update = (partial: Partial<DryBeanInputs>) => {
    onUpdate({ ...db, ...partial })
  }

  const yieldOptions: { value: DryBeanYieldRange; label: string }[] = [
    { value: '25-28', label: '25-28 Bags/A' },
    { value: '28-31', label: '28-31 Bags/A' },
    { value: '31-34', label: '31-34 Bags/A' },
    { value: '35+', label: '>35 Bags/A' },
  ]

  return (
    <div className="space-y-6">
      <p className="text-gray-600">
        These details help us build a precise dry bean program based on your operation.
      </p>

      {/* Yield Goal Range */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Yield Goal Range (Bags/A)</p>
        <div className="space-y-2">
          {yieldOptions.map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer btn-press
                ${db.yield_goal_range === opt.value ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="db_yield_range"
                checked={db.yield_goal_range === opt.value}
                onChange={() => update({ yield_goal_range: opt.value })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${db.yield_goal_range === opt.value ? 'border-yi-green' : 'border-gray-300'}`}
              >
                {db.yield_goal_range === opt.value && (
                  <div className="w-2.5 h-2.5 rounded-full bg-yi-green" />
                )}
              </div>
              <span className="font-medium text-gray-900 text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Sugar beets last year */}
      <ToggleRow
        label="Sugar beets planted last year? (zinc deficiency risk)"
        checked={db.sugar_beets_last_year}
        onChange={v => update({ sugar_beets_last_year: v })}
      />

      {/* Herbicide passes */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Herbicide Passes</p>
        <div className="space-y-2">
          {([
            { value: 1 as const, label: '1 herbicide pass' },
            { value: 2 as const, label: '2 herbicide passes' },
          ]).map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer btn-press
                ${db.herbicide_passes === opt.value ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="db_herbicide_passes"
                checked={db.herbicide_passes === opt.value}
                onChange={() => update({ herbicide_passes: opt.value })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${db.herbicide_passes === opt.value ? 'border-yi-green' : 'border-gray-300'}`}
              >
                {db.herbicide_passes === opt.value && (
                  <div className="w-2.5 h-2.5 rounded-full bg-yi-green" />
                )}
              </div>
              <span className="font-medium text-gray-900 text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Herbicide water gallons */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Herbicide Water Carrier (gal/A)
        </label>
        <input
          type="number"
          step="0.5"
          value={db.herbicide_water_gallons ?? ''}
          onChange={e => update({ herbicide_water_gallons: e.target.value ? parseFloat(e.target.value) : null })}
          placeholder="e.g., 15"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        />
        <p className="text-xs text-gray-400 mt-1">For Nano pHlow rate calculation</p>
      </div>

      {/* Fungicide passes */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Fungicide Passes</p>
        <div className="space-y-2">
          {([
            { value: 1 as const, label: '1 fungicide pass' },
            { value: 2 as const, label: '2 fungicide passes' },
          ]).map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer btn-press
                ${db.fungicide_passes === opt.value ? 'border-yi-green bg-yi-green/5' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="db_fungicide_passes"
                checked={db.fungicide_passes === opt.value}
                onChange={() => update({ fungicide_passes: opt.value })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${db.fungicide_passes === opt.value ? 'border-yi-green' : 'border-gray-300'}`}
              >
                {db.fungicide_passes === opt.value && (
                  <div className="w-2.5 h-2.5 rounded-full bg-yi-green" />
                )}
              </div>
              <span className="font-medium text-gray-900 text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Fungicide water gallons */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Fungicide Water Carrier (gal/A)
        </label>
        <input
          type="number"
          step="0.5"
          value={db.fungicide_water_gallons ?? ''}
          onChange={e => update({ fungicide_water_gallons: e.target.value ? parseFloat(e.target.value) : null })}
          placeholder="e.g., 15"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl
                     focus:border-yi-green focus:ring-1 focus:ring-yi-green"
        />
        <p className="text-xs text-gray-400 mt-1">For Nano pHlow rate calculation</p>
      </div>
    </div>
  )
}

function ToggleRow({ label, checked, onChange }: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
          ${checked ? 'bg-yi-green' : 'bg-gray-200'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
            ${checked ? 'translate-x-6' : 'translate-x-1'}`}
        />
      </button>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  )
}

function SoilStep({ formData, updateField }: {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
}) {
  const { kamariMode } = useFeatureFlags()
  const [uploadState, setUploadState] = useState<'idle' | 'parsing' | 'success' | 'error'>('idle')
  const [uploadMessage, setUploadMessage] = useState('')

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset input so the same file can be re-uploaded
    e.target.value = ''

    setUploadState('parsing')
    setUploadMessage('')

    const fd = new globalThis.FormData()
    fd.append('file', file)

    try {
      const res = await fetch('/api/soil/parse', { method: 'POST', body: fd })

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Upload failed' }))
        throw new Error(err.detail || `Server error (${res.status})`)
      }

      const report = await res.json()

      // Auto-fill form fields from parsed averages
      if (report.avg_ph != null) updateField('ph', report.avg_ph)
      if (report.avg_organic_matter_pct != null) updateField('organic_matter', report.avg_organic_matter_pct)
      if (report.avg_cec != null) updateField('cec', report.avg_cec)
      if (report.avg_phosphorus_ppm != null) updateField('phosphorus', report.avg_phosphorus_ppm)
      if (report.avg_potassium_ppm != null) updateField('potassium', report.avg_potassium_ppm)
      if (report.pct_fields_below_critical != null) updateField('pct_fields_below_critical', report.pct_fields_below_critical)
      if (report.avg_base_saturation_calcium_pct != null) updateField('base_saturation_calcium', report.avg_base_saturation_calcium_pct)
      if (report.avg_base_saturation_magnesium_pct != null) updateField('base_saturation_magnesium', report.avg_base_saturation_magnesium_pct)
      if (report.avg_base_saturation_potassium_pct != null) updateField('base_saturation_potassium', report.avg_base_saturation_potassium_pct)
      if (report.avg_calcium_ppm != null) updateField('calcium_ppm', report.avg_calcium_ppm)
      if (report.pct_fields_high_ph != null) updateField('pct_fields_high_ph', report.pct_fields_high_ph)
      if (report.pct_fields_below_critical_p != null) updateField('pct_fields_below_critical_p', report.pct_fields_below_critical_p)
      if (report.pct_fields_below_critical_k != null) updateField('pct_fields_below_critical_k', report.pct_fields_below_critical_k)
      if (report.pct_fields_low_calcium != null) updateField('pct_fields_low_calcium', report.pct_fields_low_calcium)
      if (report.avg_buffer_ph != null) updateField('buffer_ph', report.avg_buffer_ph)
      if (report.file_key) updateField('soil_file_key', report.file_key)

      // Build success message
      const parts = []
      if (report.sample_count) parts.push(`${report.sample_count} sample${report.sample_count > 1 ? 's' : ''}`)
      if (report.lab_name) parts.push(`from ${report.lab_name}`)
      if (report.field_name) parts.push(`\u2014 ${report.field_name}`)
      const summary = parts.length ? `Parsed ${parts.join(' ')}` : 'Soil test parsed'
      const suffix = report.warnings?.length ? `. ${report.warnings[0]}` : ''
      setUploadMessage(`${summary}${suffix}. Review the values below.`)
      setUploadState('success')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed'
      setUploadMessage(`${msg}. You can enter the soil data manually below.`)
      setUploadState('error')
    }
  }

  return (
    <div className="space-y-6">
      <p className="text-gray-600">
        Upload a soil test report to auto-fill, or enter values manually.
        You can skip this step if soil data isn't available.
      </p>

      {/* Skip soil toggle — kamari mode only */}
      {kamariMode && (
        <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer">
          <button
            type="button"
            role="switch"
            aria-checked={formData.skip_soil}
            onClick={() => updateField('skip_soil', !formData.skip_soil)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent
                        transition-colors duration-200 ease-in-out
                        ${formData.skip_soil ? 'bg-yi-green' : 'bg-gray-200'}`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow
                          transform transition duration-200 ease-in-out
                          ${formData.skip_soil ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </button>
          <span className="text-sm text-gray-700">Skip soil test — generate recommendations without soil data</span>
        </label>
      )}

      {formData.skip_soil ? (
        <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
          <p className="text-sm text-gray-500">
            Soil data will be skipped. Recommendations will be generated based on crop and equipment only.
          </p>
        </div>
      ) : (
      <>
      {/* Upload zone */}
      <div>
        <label
          className={`flex items-center justify-center gap-3 px-6 py-4 border-2 border-dashed
                      rounded-xl cursor-pointer transition-colors
                      ${uploadState === 'parsing'
                        ? 'border-gray-300 bg-gray-50 cursor-wait'
                        : 'border-gray-300 hover:border-yi-green hover:bg-green-50/30'}`}
        >
          {uploadState === 'parsing' ? (
            <>
              <Loader2 className="w-5 h-5 text-yi-green animate-spin" />
              <span className="text-sm text-gray-600">Parsing soil test...</span>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">
                Upload soil test report
                <span className="text-xs text-gray-400 ml-1">(PDF, JPG, PNG)</span>
              </span>
            </>
          )}
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={handleFileUpload}
            disabled={uploadState === 'parsing'}
            className="hidden"
          />
        </label>
      </div>

      {/* Status banner */}
      {uploadState === 'success' && (
        <div className="flex items-start gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
          <FileCheck className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-green-800">{uploadMessage}</p>
        </div>
      )}
      {uploadState === 'error' && (
        <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <X className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">{uploadMessage}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phosphorus (ppm)
          </label>
          <input
            type="number"
            value={formData.phosphorus || ''}
            onChange={e => updateField('phosphorus', parseFloat(e.target.value) || 0)}
            placeholder="e.g., 25"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
          <p className="text-xs text-gray-400 mt-1">Mehlich-3</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Potassium (ppm)
          </label>
          <input
            type="number"
            value={formData.potassium || ''}
            onChange={e => updateField('potassium', parseFloat(e.target.value) || 0)}
            placeholder="e.g., 180"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Organic Matter (%)
          </label>
          <input
            type="number"
            step="0.1"
            value={formData.organic_matter || ''}
            onChange={e => updateField('organic_matter', parseFloat(e.target.value) || 0)}
            placeholder="e.g., 3.5"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CEC
          </label>
          <input
            type="number"
            step="0.1"
            value={formData.cec || ''}
            onChange={e => updateField('cec', parseFloat(e.target.value) || 0)}
            placeholder="e.g., 12"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Soil pH
          </label>
          <input
            type="number"
            step="0.1"
            value={formData.ph || ''}
            onChange={e => updateField('ph', parseFloat(e.target.value) || 0)}
            placeholder="e.g., 6.5"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fields Below Critical (%)
          </label>
          <input
            type="number"
            value={formData.pct_fields_below_critical || ''}
            onChange={e => updateField('pct_fields_below_critical', parseFloat(e.target.value) || 0)}
            placeholder="e.g., 25"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
        </div>
      </div>

      {/* Base Saturation fields */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Base Saturation (optional)</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Calcium (%)</label>
            <input
              type="number"
              step="0.1"
              value={formData.base_saturation_calcium ?? ''}
              onChange={e => updateField('base_saturation_calcium', e.target.value ? parseFloat(e.target.value) : null)}
              placeholder="Ca"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                         focus:border-yi-green focus:ring-1 focus:ring-yi-green"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Magnesium (%)</label>
            <input
              type="number"
              step="0.1"
              value={formData.base_saturation_magnesium ?? ''}
              onChange={e => updateField('base_saturation_magnesium', e.target.value ? parseFloat(e.target.value) : null)}
              placeholder="Mg"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                         focus:border-yi-green focus:ring-1 focus:ring-yi-green"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Potassium (%)</label>
            <input
              type="number"
              step="0.1"
              value={formData.base_saturation_potassium ?? ''}
              onChange={e => updateField('base_saturation_potassium', e.target.value ? parseFloat(e.target.value) : null)}
              placeholder="K"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                         focus:border-yi-green focus:ring-1 focus:ring-yi-green"
            />
          </div>
        </div>
      </div>

      {/* Buffer pH — kamari mode only */}
      {kamariMode && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Buffer pH
          </label>
          <input
            type="number"
            step="0.1"
            value={formData.buffer_ph ?? ''}
            onChange={e => updateField('buffer_ph', e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="e.g., 6.6"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl
                       focus:border-yi-green focus:ring-1 focus:ring-yi-green"
          />
          <p className="text-xs text-gray-400 mt-1">SMP or Sikora buffer pH</p>
        </div>
      )}

      {/* Detailed Soil Breakdown (collapsible) */}
      <DetailedSoilBreakdown formData={formData} updateField={updateField} />
      </>
      )}
    </div>
  )
}

function DetailedSoilBreakdown({ formData, updateField }: {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium text-gray-700">Detailed Soil Breakdown (optional)</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          <p className="text-xs text-gray-500">
            For more precise corn recommendations, enter nutrient-specific thresholds.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Calcium (ppm)</label>
              <input
                type="number"
                value={formData.calcium_ppm ?? ''}
                onChange={e => updateField('calcium_ppm', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="e.g., 180"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                           focus:border-yi-green focus:ring-1 focus:ring-yi-green"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">% Fields pH &ge; 6.9</label>
              <input
                type="number"
                value={formData.pct_fields_high_ph ?? ''}
                onChange={e => updateField('pct_fields_high_ph', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="e.g., 40"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                           focus:border-yi-green focus:ring-1 focus:ring-yi-green"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">% Below Critical P</label>
              <input
                type="number"
                value={formData.pct_fields_below_critical_p ?? ''}
                onChange={e => updateField('pct_fields_below_critical_p', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="e.g., 35"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                           focus:border-yi-green focus:ring-1 focus:ring-yi-green"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">% Below Critical K</label>
              <input
                type="number"
                value={formData.pct_fields_below_critical_k ?? ''}
                onChange={e => updateField('pct_fields_below_critical_k', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="e.g., 20"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                           focus:border-yi-green focus:ring-1 focus:ring-yi-green"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">% Fields Low Calcium</label>
              <input
                type="number"
                value={formData.pct_fields_low_calcium ?? ''}
                onChange={e => updateField('pct_fields_low_calcium', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="e.g., 30"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                           focus:border-yi-green focus:ring-1 focus:ring-yi-green"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
